// React
import { useState, FormEvent } from 'react';

// Hooks
import { useHubSpotForm } from '../../hooks/useHubSpotForm';

// Utils
import { cn } from '../../utils/cn';
import { isPersonalEmail } from '../../utils/emailValidation';

// Types
import type { HubSpotFormData } from '../../lib/sanity/types';

type ArticleHubSpotFormProps = {
  formConfig: HubSpotFormData;
  className?: string;
};

/**
 * ArticleHubSpotForm - HubSpot form component for articles
 * Renders a form with fixed fields (first name, last name, email) and submits to HubSpot
 * The form ID is configured per-article in Sanity
 */
export const ArticleHubSpotForm = ({ formConfig, className }: ArticleHubSpotFormProps) => {
  const {
    formId,
    title = 'Get the Full Guide',
    description,
    buttonText = 'Download',
    successMessage = 'Thank you! Check your email for the resource.',
    image,
  } = formConfig;

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Use the existing HubSpot hook with the article-specific formId
  // formId is guaranteed to exist when this component is rendered (checked in parent)
  const { submit, isSubmitting, isSuccess, error } = useHubSpotForm({
    portalId: import.meta.env.VITE_HUBSPOT_PORTAL_ID || '',
    formId: formId || '',
    region: import.meta.env.VITE_HUBSPOT_REGION || 'na1',
  });

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    } else if (isPersonalEmail(formData.email)) {
      newErrors.email = 'Please use your work email address.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) return;

    // Map form fields to HubSpot field names
    const fields = [
      { name: 'firstname', value: formData.firstName },
      { name: 'lastname', value: formData.lastName },
      { name: 'email', value: formData.email },
    ];

    const success = await submit(fields);
    if (success) {
      setFormData({ firstName: '', lastName: '', email: '' });
    }
  };

  // Success state
  if (isSuccess) {
    return (
      <div
        className={cn(
          'flex items-center justify-center bg-blue-1000 rounded-12 p-[var(--spacing-32)]',
          className
        )}
      >
        <div className="flex items-center gap-[var(--spacing-16)] max-sm:flex-col max-sm:text-center">
          <div className="w-10 h-10 rounded-full bg-green-1000/20 flex items-center justify-center shrink-0">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-green-1000"
              aria-hidden="true"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div className="flex flex-col gap-[var(--spacing-4)]">
            <h3 className="font-heading text-[24px] leading-[32px] font-light text-orange-200">
              Success!
            </h3>
            <p className="font-body text-[14px] leading-[20px] text-gray-200">{successMessage}</p>
          </div>
        </div>
      </div>
    );
  }

  const inputClasses = cn(
    'w-full h-[56px] px-[var(--spacing-16)] py-[var(--spacing-16)]',
    'bg-transparent border border-gray-200 rounded-8',
    'font-body text-[16px] leading-[24px] text-white placeholder:text-gray-600',
    'focus:outline-none focus:border-blue-800 transition-colors'
  );

  return (
    <div
      className={cn(
        'flex w-full bg-blue-1000 rounded-12 overflow-hidden shadow-background',
        'max-lg:flex-col',
        className
      )}
    >
      {/* Optional Image */}
      {image?.url && (
        <div className="w-full max-w-[336px] min-w-[200px] shrink-0 p-[var(--spacing-16)] max-lg:max-w-full">
          <img
            src={image.url}
            alt={image.alt || 'Form image'}
            className="w-full h-full min-h-[280px] object-cover rounded-12 max-lg:aspect-video max-lg:min-h-0"
          />
        </div>
      )}

      {/* Form Section */}
      <div className="flex flex-col gap-[var(--spacing-16)] p-[var(--spacing-32)] flex-1 min-w-0 max-lg:p-[var(--spacing-24)]">
        {/* Title */}
        <h3 className="font-heading text-[32px] leading-[40px] font-light text-orange-200">
          {title}
        </h3>

        {/* Description */}
        {description && (
          <p className="font-body text-[16px] leading-[24px] text-gray-200">{description}</p>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-[var(--spacing-16)]">
          <div>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="First Name"
              aria-label="First Name"
              required
              className={cn(inputClasses, errors.firstName && 'border-red-1000')}
            />
            {errors.firstName && (
              <p className="mt-1 font-body text-[12px] text-red-1000" role="alert">
                {errors.firstName}
              </p>
            )}
          </div>

          <div>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Last Name"
              aria-label="Last Name"
              required
              className={cn(inputClasses, errors.lastName && 'border-red-1000')}
            />
            {errors.lastName && (
              <p className="mt-1 font-body text-[12px] text-red-1000" role="alert">
                {errors.lastName}
              </p>
            )}
          </div>

          <div>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Email"
              aria-label="Email"
              required
              className={cn(inputClasses, errors.email && 'border-red-1000')}
            />
            {errors.email && (
              <p className="mt-1 font-body text-[12px] text-red-1000" role="alert">
                {errors.email}
              </p>
            )}
          </div>

          {/* Global Error */}
          {error && (
            <p className="font-body text-[14px] text-red-1000" role="alert">
              {error}
            </p>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className={cn(
              'w-full inline-flex items-center justify-center',
              'px-[var(--spacing-16)] py-[var(--spacing-12)] rounded-8',
              'bg-orange-800 text-white',
              'font-body text-[16px] leading-[24px] font-normal',
              'hover:bg-orange-1000 transition-colors duration-200',
              'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-800',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {isSubmitting ? 'Submitting...' : buttonText}
          </button>
        </form>
      </div>
    </div>
  );
};
